/**
 * 파일명 : evl0103m.tsx
 * 설명 : 인사평가 결과 집계 화면 - 모바일
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용
 ************************************************
 * 1.0       2026-01-20   변재석     최초작성
 * 1.1       2026-01-26   변재석     서버 계산 로직으로 변경 (프론트 계산 제거)
 * 1.2       2026-02-02   조동현     모바일 버전 생성
 ************************************************/

import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Button,
  Select,
  Input,
  Table,
  Modal,
  message,
  Typography,
  Empty,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { DownloadOutlined, EditOutlined } from "@ant-design/icons";
import * as common from "libs/common";
import * as commonProxy from "libs/CommonProxy";
import { getevlTypeMenu } from "./evl0101m";
import * as XLSX from "xlsx";
import { isMobile } from "react-device-detect";
import type {
  IProjectEvaluation,
  IEvaluationDetail,
  IScoreHistory,
  IEvaluationRecord,
} from "../../../redux/models/evl/evl0103m_model";
import { calculatePositionTenure } from "../../../redux/slice/evl/evl0103m_slice";

const { Title } = Typography;
const { Option } = Select;

// 평가 데이터 타입 정의 (Redux model 기반)
type ProjectEvaluation = IProjectEvaluation;
type EvaluationDetail = IEvaluationDetail;
type ScoreHistory = IScoreHistory;
type EvaluationRecord = IEvaluationRecord;

const MobileEvl0103m: React.FC = () => {
  const isHrAdmin = common.isHrAdmin();
  const [evaluationYears, setEvaluationYears] = useState<any[]>([]);
  const [evaluationData, setEvaluationData] = useState<EvaluationRecord[]>([]);
  const [filteredData, setFilteredData] = useState<EvaluationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState<string | undefined>(
    undefined,
  );
  const [evaluateeName, setEvaluateeName] = useState<string | undefined>(
    undefined,
  );
  const [department, setDepartment] = useState<string>("");
  const [position, setPosition] = useState<string>("");

  // 평가기간 구분 (HR: 인사평가기간, PLG: 업무서약기간)
  const [period, setPeriod] = useState<string>("");
  // 임원 여부 (본부장/사업부장/CEO - 직책코드 04/05/09)
  const [isExecutive, setIsExecutive] = useState(false);

  // 검색 후 서버 조회 시 로컬필터 자동 적용을 위한 플래그
  const [pendingApplyFilter, setPendingApplyFilter] = useState<boolean>(false);

  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  React.useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 최종점수 수정 모달 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<EvaluationRecord | null>(
    null,
  );
  const [newScore, setNewScore] = useState<string>("0");
  const [scoreReason, setScoreReason] = useState<string>("");
  const scoreReasonRef = useRef<string>(""); // 입력 버벅거림 방지용 ref
  const [scoreReasonCount, setScoreReasonCount] = useState<number>(0);
  const [resetKey, setResetKey] = useState<number>(0); // TextArea 초기화용 key
  const [isResetMode, setIsResetMode] = useState<boolean>(false); // 초기화 모드 여부

  // 인사평가는 별도 페이지로 이동하여 조회합니다.

  const messageApi = message;

  // 수정 모달 열기
  const openScoreModal = (record: EvaluationRecord) => {
    setEditingRecord(record);
    setNewScore(String(record.finalScore));
    const initialReason = record.scrAdjRsn || "";
    setScoreReason(initialReason); // 기존 수정 사유 표시
    scoreReasonRef.current = initialReason; // ref도 초기화
    setScoreReasonCount(initialReason.length);
    setIsModalOpen(true);
  };

  // 수정 모달 닫기
  const closeScoreModal = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
    setNewScore("0");
    setScoreReason("");
    scoreReasonRef.current = ""; // ref도 초기화
    setScoreReasonCount(0);
    setIsResetMode(false);
  };

  // 수정 모달 초기화 (2차평가 점수로 복원, 사유 비움)
  const resetScoreModal = () => {
    if (!editingRecord) return;
    const evl2Score = editingRecord.secondEvaluation?.total ?? 0;
    Modal.confirm({
      title: "초기화",
      content: `최종점수가 2차평가 점수(${evl2Score}점)로 복원되고, 수정사유가 비워집니다. 초기화가 즉시 반영됩니다.`,
      okText: "초기화",
      cancelText: "취소",
      onOk: () => {
        setNewScore(String(evl2Score));
        setScoreReason("");
        scoreReasonRef.current = "";
        setScoreReasonCount(0);
        setResetKey((prev) => prev + 1);
        setIsResetMode(true);

        const requestData = {
          empNo: editingRecord.empNo,
          evlYear: editingRecord.evaluationYear,
          fnlScr: evl2Score,
          scrAdjRsn: "",
          reset: true,
        };
        commonProxy.callAsyncSvc(requestData, {
          txCd: "TREVLS0018",
          success: (responseData: any) => {
            const data = responseData.data || responseData;
            if (data.resultCode === "400") {
              message.error(data.resultMsg || "초기화에 실패했습니다.");
              return;
            }
            message.success("최종점수가 수정되었습니다.");
            closeScoreModal();
            fetchEvaluationData({
              evaluationYear: editingRecord.evaluationYear,
            });
          },
          error: (errorMsg: string) => {
            message.error(errorMsg || "초기화에 실패했습니다.");
          },
        });
      },
    });
  };

  // 최종점수 수정 저장
  const handleSaveScore = async () => {
    if (!editingRecord) return;

    const reason = scoreReasonRef.current; // ref에서 값 가져오기
    const numScoreForCheck = parseFloat(newScore || "0");

    // 초기화 모드가 아닌 경우에만 수정 사유 필수
    if (!isResetMode && numScoreForCheck > 0 && !reason.trim()) {
      message.error("수정 사유를 입력해주세요.");
      return;
    }

    // 새로운 점수 유효성 검사 (decimal 5,2 형식: 정수 3자리, 소수점 2자리)
    const scoreDecimalRegex = /^\d{1,3}(\.\d{1,2})?$/;
    if (!scoreDecimalRegex.test(newScore)) {
      message.error("점수는 100.00 이하의 소수점 2자리까지만 입력 가능합니다.");
      return;
    }

    const numScore = parseFloat(newScore);

    // 점수 범위 검증 (0 ~ 100.00)
    if (isNaN(numScore) || numScore < 0 || numScore > 100.0) {
      message.error("점수는 0 이상 100.00 이하여야 합니다.");
      return;
    }

    // TREVLS0018 호출 - 최종점수 수정
    const requestData = {
      empNo: editingRecord.empNo, // key가 아닌 empNo 사용
      evlYear: editingRecord.evaluationYear,
      fnlScr: numScore,
      scrAdjRsn: isResetMode ? "" : reason, // 초기화 시 사유 비움
      reset: isResetMode, // 초기화 여부 플래그
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLS0018",
      success: (responseData: any) => {
        const data = responseData.data || responseData;
        const resultCode = data.resultCode;
        const resultMsg = data.resultMsg;

        if (resultCode === "400") {
          message.error(resultMsg || "최종점수 수정에 실패했습니다.");
          return;
        }

        message.success("최종점수가 수정되었습니다.");
        closeScoreModal();

        // TREVLR0018 다시 호출 - 업데이트된 데이터 조회
        setPendingApplyFilter(true);
        fetchEvaluationData({ evaluationYear: editingRecord.evaluationYear });
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || "최종점수 수정에 실패했습니다.");
      },
    });
  };

  useEffect(() => {
    fetchEvaluationYears();
  }, []);

  // 등급 필터 변경 시 적용
  useEffect(() => {
    if (evaluationData.length > 0) {
      applyLocalFilter();
    }
  }, [evaluationData]);

  const fetchEvaluationYears = () => {
    commonProxy.callAsyncSvc(
      {},
      {
        txCd: "TREVLR0016",
        success: (responseData: any) => {
          const data = responseData.data || responseData;
          const evlYears = data.evlYears || [];
          if (Array.isArray(evlYears) && evlYears.length > 0) {
            setEvaluationYears(evlYears);

            // ✅ 올해 자동 선택 로직 추가
            const thisYear = String(new Date().getFullYear());
            const hasThisYear = evlYears.some(
              (y: any) => y.evlYear === thisYear || y.value === thisYear,
            );
            if (hasThisYear) {
              setSelectedYear(thisYear);
              fetchEvaluationData({ evaluationYear: thisYear }); // 자동 조회 실행
            }
          }
        },
        error: (errorMsg: string) => {
          messageApi.error(errorMsg || "평가년도 조회에 실패했습니다.");
        },
      },
    );
  };

  const fetchEvaluationData = (params?: { evaluationYear?: string }) => {
    setLoading(true);
    const empNo = common.getEmpNo();
    const requestData = {
      empNo: empNo,
      evlYear: params?.evaluationYear || "",
      evaluateeName: "",
      evaluatorName: "",
      position: "",
      department: "",
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0018",
      success: (responseData: any) => {
        try {
          const data = responseData.data || responseData;
          const list = data.list || [];
          const records: EvaluationRecord[] = list.map(
            (item: any, index: number) => ({
              key: `${item.empNo}_${index}`, // 중복 방지를 위해 index 추가
              empNo: item.empNo || "", // 사원번호 별도 저장
              evaluateeName: item.userNm || "",
              evaluatorName: item.evaluatorName || "",
              department: item.deptNm || "",
              position: item.postNm || "",
              positionTenure: calculatePositionTenure(item.currPosStDt),
              evaluationYear: item.evlYear || "",
              finalScore: item.fnlScr || 0,
              scrAdjRsn: item.scrAdjRsn || "",
              eval2SubYn: item.eval2SubYn || "N", // 2차평가 제출여부
              slfSubYn: item.slfSubYn || "N", // 자기평가 제출여부
              eval1SubYn: item.eval1SubYn || "N", // 1차평가 제출여부
              bizPlgSubYn: item.bizPlgSubYn || "N", // 업무서약서 제출여부
              finalScoreHistory: [] as ScoreHistory[],
              selfEvaluation: {
                performance: item.selfPerf || 0,
                commonCompetency: item.selfCommon || 0,
                roleCompetency: item.selfRole || 0,
                jobCompetency: item.selfJob || 0,
                total: item.selfTotal || 0,
                comment: "",
              },
              projectEvaluation: {
                common: 0,
                jobSpecific: 0,
                total: 0,
                comment: "",
              },
              firstEvaluation: {
                performance: item.evl1Perf || 0,
                commonCompetency: item.evl1Common || 0,
                roleCompetency: item.evl1Role || 0,
                jobCompetency: item.evl1Job || 0,
                total: item.evl1Total || 0,
                comment: item.evl1Rmk || "",
              },
              secondEvaluation: {
                performance: item.evl2Perf || 0,
                commonCompetency: item.evl2Common || 0,
                roleCompetency: item.evl2Role || 0,
                jobCompetency: item.evl2Job || 0,
                total: item.evl2Total || 0,
                comment: item.evl2Rmk || "",
              },
              hrMapId: item.hrMapId || "",
              projectEvaluations: (item.projects || []).map(
                (p: any, idx: number) => ({
                  key: `${p.pjMapId}_${p.evlCd}_${idx}`,
                  pjMapId: p.pjMapId || "",
                  evlCd: p.evlCd || "",
                  pmSubYn: p.pmSubYn || "N", // 프로젝트평가 제출여부
                  projectName: p.projectNm || "",
                  grade: "",
                  description: "",
                  period: "",
                  common: p.commonSum || 0,
                  jobSpecific: p.jobSum || 0,
                  total: p.totalSum || 0,
                  comment: p.pjEvlRmk || "",
                }),
              ),
              isEval1: item.isEval1 || "N",
              isEval2: item.isEval2 || "N",
              canViewSecondEval: item.canViewSecondEval || "N",
            }),
          );
          setEvaluationData(records);
          setFilteredData(records);
          // 평가기간 구분 설정
          setPeriod(data.period || "PLG");
          // 임원 여부 설정 (본부장/사업부장/CEO)
          setIsExecutive(data.isExecutive === true || data.isExecutive === "Y");
          setLoading(false);

          // 검색 버튼으로 호출되었을 때는 서버 응답 후 로컬 필터를 적용
          if (pendingApplyFilter) {
            setPendingApplyFilter(false);
            applyLocalFilter();
          }

          if (records.length === 0) {
            messageApi.info("조회된 데이터가 없습니다.");
          } else {
            messageApi.success(`조회되었습니다.`);
          }
        } catch (error) {
          setLoading(false);
          messageApi.error("데이터 처리 중 오류가 발생했습니다.");
        }
      },
      error: (errorMsg: string) => {
        setLoading(false);
        messageApi.error(errorMsg || "데이터 조회에 실패했습니다.");
      },
    });
  };

  // 로컬 필터 적용
  const applyLocalFilter = () => {
    let filtered = evaluationData;
    if (evaluateeName)
      filtered = filtered.filter((r) =>
        r.evaluateeName.includes(evaluateeName),
      );
    if (department)
      filtered = filtered.filter((r) => r.department.includes(department));
    if (position) filtered = filtered.filter((r) => r.position === position);

    setFilteredData(filtered);
  };

  const handleSearch = () => {
    // 년도 미선택 시 경고 및 표 초기화 (Empty 표시)
    if (!selectedYear) {
      messageApi.warning("검색년도를 입력하십시오");
      setFilteredData([]);
      return;
    }

    // 이미 해당 연도 데이터가 로드되어 있으면 로컬 필터 적용
    if (evaluationData.some((r) => r.evaluationYear === selectedYear)) {
      applyLocalFilter();
      return;
    }

    // 데이터가 없으면 서버에서 로드한 뒤 필터 적용
    setPendingApplyFilter(true);
    fetchEvaluationData({ evaluationYear: selectedYear });
  };

  const handleReset = () => {
    const currentYear = selectedYear;
    setSelectedYear(currentYear);
    setEvaluateeName(undefined);
    setDepartment("");
    setPosition("");
    setFilteredData(evaluationData);
    messageApi.info("필터가 초기화되었습니다.");
  };

  const handleExcelDownload = () => {
    if (filteredData.length === 0) {
      messageApi.warning("다운로드할 데이터가 없습니다.");
      return;
    }

    try {
      // 워크북 생성
      const wb = XLSX.utils.book_new();

      // 헤더 행 구성
      const headerRow1 = [
        "이름",
        "최종점수",
        "부서",
        "직급",
        "직급 근무기간",
        "자기평가",
        "",
        "",
        "",
        "",
        "프로젝트평가",
        "",
        "",
        "",
        "",
        "1차평가",
        "",
        "",
        "",
        "",
        "",
        "2차평가",
        "",
        "",
        "",
        "",
        "",
      ];

      const headerRow2 = [
        "",
        "",
        "",
        "",
        "",
        "업적평가",
        "공통역량",
        "역할역량",
        "직무역량",
        "계",
        "프로젝트명",
        "공통",
        "직무별",
        "계",
        "평가의견",
        "업적평가",
        "공통역량",
        "역할역량",
        "직무역량",
        "계",
        "평가의견",
        "업적평가",
        "공통역량",
        "역할역량",
        "직무역량",
        "계",
        "평가의견",
      ];

      // 데이터 행 생성
      const dataRows: any[] = [];

      filteredData.forEach((record) => {
        const projectCount = record.projectEvaluations.length;
        const maxRows = Math.max(1, projectCount); // 최소 1행
        for (let i = 0; i < maxRows; i++) {
          const project = record.projectEvaluations[i];
          // 최종점수는 관리자만, 2차평가는 관리자+2차평가자/임원
          const canViewFinal = isHrAdmin;
          const canView2nd = isHrAdmin || record.canViewSecondEval === "Y";

          const row = [
            // 첫 번째 프로젝트 행에만 기본 정보 표시
            i === 0 ? record.evaluateeName : "",
            i === 0 ? (canViewFinal ? record.finalScore : "-") : "",
            i === 0 ? record.department : "",
            i === 0 ? record.position : "",
            i === 0 ? record.positionTenure : "",
            // 자기평가
            i === 0 ? record.selfEvaluation.performance : "",
            i === 0 ? record.selfEvaluation.commonCompetency : "",
            i === 0 ? record.selfEvaluation.roleCompetency : "",
            i === 0 ? record.selfEvaluation.jobCompetency : "",
            i === 0 ? record.selfEvaluation.total : "",
            // 프로젝트평가
            project ? project.projectName : "",
            project ? project.common : "",
            project ? project.jobSpecific : "",
            project ? project.total : "",
            project && project.pmSubYn === "Y" ? project.comment : "",
            // 1차평가
            i === 0 ? record.firstEvaluation.performance : "",
            i === 0 ? record.firstEvaluation.commonCompetency : "",
            i === 0 ? record.firstEvaluation.roleCompetency : "",
            i === 0 ? record.firstEvaluation.jobCompetency : "",
            i === 0 ? record.firstEvaluation.total : "",
            i === 0 && record.eval1SubYn === "Y"
              ? record.firstEvaluation.comment
              : "",
            // 2차평가
            i === 0
              ? canView2nd
                ? record.secondEvaluation.performance
                : "-"
              : "",
            i === 0
              ? canView2nd
                ? record.secondEvaluation.commonCompetency
                : "-"
              : "",
            i === 0
              ? canView2nd
                ? record.secondEvaluation.roleCompetency
                : "-"
              : "",
            i === 0
              ? canView2nd
                ? record.secondEvaluation.jobCompetency
                : "-"
              : "",
            i === 0 ? (canView2nd ? record.secondEvaluation.total : "-") : "",
            i === 0 && canView2nd && record.eval2SubYn === "Y"
              ? record.secondEvaluation.comment
              : "",
          ];

          dataRows.push(row);
        }
      });

      // 워크시트 데이터 생성
      const wsData = [headerRow1, headerRow2, ...dataRows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // 컬럼 너비 설정
      ws["!cols"] = [
        { wch: 10 }, // 이름
        { wch: 10 }, // 최종점수
        { wch: 12 }, // 부서
        { wch: 8 }, // 직급
        { wch: 14 }, // 직급 근무기간
        // 자기평가
        { wch: 10 },
        { wch: 10 },
        { wch: 10 },
        { wch: 10 },
        { wch: 8 },
        // 프로젝트평가
        { wch: 20 },
        { wch: 8 },
        { wch: 8 },
        { wch: 8 },
        { wch: 30 },
        // 1차평가
        { wch: 10 },
        { wch: 10 },
        { wch: 10 },
        { wch: 10 },
        { wch: 8 },
        { wch: 30 },
        // 2차평가
        { wch: 10 },
        { wch: 10 },
        { wch: 10 },
        { wch: 10 },
        { wch: 8 },
        { wch: 30 },
      ];

      // 병합 셀 설정
      const merges: XLSX.Range[] = [
        // 1행 병합 (대분류 헤더)
        { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // 이름
        { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // 최종점수
        { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } }, // 부서
        { s: { r: 0, c: 3 }, e: { r: 1, c: 3 } }, // 직급
        { s: { r: 0, c: 4 }, e: { r: 1, c: 4 } }, // 직급 근무기간
        { s: { r: 0, c: 5 }, e: { r: 0, c: 9 } }, // 자기평가
        { s: { r: 0, c: 10 }, e: { r: 0, c: 14 } }, // 프로젝트평가
        { s: { r: 0, c: 15 }, e: { r: 0, c: 20 } }, // 1차평가
        { s: { r: 0, c: 21 }, e: { r: 0, c: 26 } }, // 2차평가
      ];

      // 데이터 행의 병합 처리 (프로젝트가 여러 개인 경우)
      let currentRow = 2; // 데이터는 3행부터 시작 (0-based index)
      filteredData.forEach((record) => {
        const projectCount = record.projectEvaluations.length;
        const maxRows = Math.max(1, projectCount);

        if (maxRows > 1) {
          // 기본 정보 컬럼 병합 (이름, 최종점수, 부서, 직급, 직급 근무기간)
          for (let col = 0; col < 5; col++) {
            merges.push({
              s: { r: currentRow, c: col },
              e: { r: currentRow + maxRows - 1, c: col },
            });
          }
          // 자기평가 컬럼 병합
          for (let col = 5; col < 10; col++) {
            merges.push({
              s: { r: currentRow, c: col },
              e: { r: currentRow + maxRows - 1, c: col },
            });
          }
          // 1차평가 컬럼 병합
          for (let col = 15; col < 21; col++) {
            merges.push({
              s: { r: currentRow, c: col },
              e: { r: currentRow + maxRows - 1, c: col },
            });
          }
          // 2차평가 컬럼 병합
          for (let col = 21; col < 27; col++) {
            merges.push({
              s: { r: currentRow, c: col },
              e: { r: currentRow + maxRows - 1, c: col },
            });
          }
        }

        currentRow += maxRows;
      });

      ws["!merges"] = merges;

      // 워크북에 워크시트 추가
      XLSX.utils.book_append_sheet(wb, ws, "최종 평가 결과");

      // 파일 다운로드
      const date = new Date().toISOString().split("T")[0];
      XLSX.writeFile(wb, `최종_평가_결과_${date}.xlsx`);

      messageApi.success("엑셀 파일이 다운로드되었습니다.");
    } catch (error) {
      messageApi.error("엑셀 다운로드 중 오류가 발생했습니다.");
    }
  };

  const openEvl0102m = (record: EvaluationRecord) => {
    const param = {
      menuId: getevlTypeMenu("업무서약서"),
      props: {
        empNo: record.empNo || "",
        evlYear: record.evaluationYear,
        evlEnableYn: "N",
        viewMode: "readonly",
      },
    };

    common.openPage(param);
  };

  const openEvl0105m = (record: EvaluationRecord) => {
    const empNoToUse = common.getEmpNo();

    const param = {
      menuId: getevlTypeMenu("인사평가-자기평가"),
      props: {
        evlId: record.hrMapId || "",
        evlType: "인사평가-자기평가",
        empNo: empNoToUse,
        hrMapId: record.hrMapId || "",   
        evlEnableYn: "N",
        viewMode: "readonly",
        evlYear: record.evaluationYear,
        canViewSecondEval: record.canViewSecondEval,
        evlEditableYn: "N",
      },
    };

    common.openPage(param);
  };

  /**
   * 컬럼 표시 조건:
   * - 관리자(isHrAdmin): 모든 컬럼 표시, 최종점수 수정 가능
   * - 2차평가자 또는 임원(canViewSecondEval='Y'): 2차평가까지 표시 (최종점수 숨김)
   * - 1차평가자(isEval1='Y'): 1차평가까지만 표시 (2차평가, 최종점수 숨김)
   */
  const columns: ColumnsType<EvaluationRecord> = [
    {
      title: <div style={{ fontSize: "10px" }}>이름</div>,
      dataIndex: "evaluateeName",
      key: "evaluateeName",
      render: (value: string) => (
        <div style={{ fontSize: "10px", fontWeight: 500, textAlign: "center" }}>
          {value}
        </div>
      ),
      width: 130,
      align: "center" as const,
    },
    {
      title: <div style={{ fontSize: "10px" }}>부서</div>,
      dataIndex: "department",
      key: "department",
      render: (value: string) => (
        <div style={{ fontSize: "10px", fontWeight: 500, textAlign: "center" }}>
          {value}
        </div>
      ),
      width: 150,
      align: "center" as const,
    },
    {
      title: <div style={{ fontSize: "10px" }}>업무서약서</div>,
      key: "workPledge",
      dataIndex: "empNo",
      render: (_, record) => {
        const canOpen = record.bizPlgSubYn === "Y";
        return (
          <div style={{ textAlign: "center" }}>
            {!canOpen ? (
              <span style={{ fontSize: "12px", color: "#999" }}>
                <Button
                  type="link"
                  size="small"
                  onClick={undefined}
                  style={{
                    fontSize: "12px",
                    color: "#999",
                    cursor: "default",
                  }}
                >
                  보기
                </Button>
              </span>
            ) : (
              <Button
                type="link"
                size="small"
                onClick={() => openEvl0102m(record)}
                style={{ fontSize: "12px" }}
              >
                보기
              </Button>
            )}
          </div>
        );
      },
      width: 120,
      align: "center" as const,
    },
    {
      title: <div style={{ fontSize: "10px" }}>인사평가</div>,
      key: "personnelEvl",
      dataIndex: "empNo",
      render: (_, record) => {
        let canOpen = false;
        if (period !== "HR" || !record.hrMapId) {
          canOpen = false;
        } else if (isHrAdmin) {
          canOpen = record.eval2SubYn === "Y";
        } else if (isExecutive && record.isEval1 === "Y") {
          canOpen = record.slfSubYn === "Y";
        } else if (isExecutive && record.isEval2 === "Y") {
          canOpen = record.eval1SubYn === "Y";
        } else if (isExecutive) {
          canOpen = record.eval2SubYn === "Y";
        } else if (record.isEval2 === "Y") {
          canOpen = record.eval1SubYn === "Y";
        } else if (record.isEval1 === "Y") {
          canOpen = record.slfSubYn === "Y";
        }
        return (
          <div style={{ textAlign: "center" }}>
            {!canOpen ? (
              <span style={{ fontSize: "12px", color: "#999" }}>
                <Button
                  type="link"
                  size="small"
                  onClick={undefined}
                  style={{
                    fontSize: "12px",
                    color: "#999",
                    cursor: "default",
                  }}
                >
                  보기
                </Button>
              </span>
            ) : (
              <Button
                type="link"
                size="small"
                onClick={() => openEvl0105m(record)}
                style={{ fontSize: "12px" }}
              >
                보기
              </Button>
            )}
          </div>
        );
      },
      width: 120,
      align: "center" as const,
    },
    {
      title: <div style={{ fontSize: "10px" }}>최종점수</div>,
      dataIndex: "finalScore",
      key: "finalScore",
      width: 130,
      align: "center" as const,
      render: (value: number, record) => {
        const canView = isHrAdmin;
        const isExpired =
          new Date() > new Date(`${record.evaluationYear}-12-31T23:59:59`);
        const canEdit = isHrAdmin && !isExpired;

        if (!canView) {
          return <span style={{ fontSize: "12px", color: "#999" }}>-</span>;
        }

        const isReady = canEdit && record.eval2SubYn === "Y";
        const btnLabel = isExpired
          ? "마감"
          : record.eval2SubYn === "Y"
            ? "수정"
            : "진행중";
        const btnTitle = isExpired
          ? `수정 기한 만료 (${record.evaluationYear}-12-31)`
          : record.eval2SubYn !== "Y"
            ? "2차평가 미제출"
            : "";

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <span style={{ fontWeight: "500", fontSize: "13px" }}>{value}</span>
            {isHrAdmin && (
              <Button
                type={isReady ? "primary" : "default"}
                size="small"
                icon={isReady ? <EditOutlined /> : undefined}
                onClick={() => isReady && openScoreModal(record)}
                style={{
                  fontSize: "11px",
                  color: !isReady ? "#999" : undefined,
                }}
                disabled={!isReady}
                title={btnTitle}
              >
                {btnLabel}
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  // 옵션 생성
  const departmentOptions = Array.from(
    new Set(evaluationData.map((d) => d.department)),
  ).filter(Boolean);
  const positionOptions = Array.from(
    new Set(evaluationData.map((d) => d.position)),
  ).filter(Boolean);

  return (
    <div
      style={{
        padding: isMobile ? "12px" : "20px",
        marginTop: "30px",
        backgroundColor: "#f5f5f5",
        width: screenWidth - 20 + "px",
        paddingBottom: "10px",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <Title level={4} style={{ margin: 10 }}>
          최종 평가 결과
        </Title>
      </div>

      <Card size="small" style={{ marginBottom: 12 }}>
        {/* 
        <div style={{ marginBottom: 8 }}>
          <Text strong>검색 필터</Text>
        </div> */}
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <div style={{ width: 120 }}>
            <div style={{ fontSize: "11px", marginBottom: 4 }}>평가년도</div>
            <Select
              placeholder="년도 선택"
              value={selectedYear}
              onChange={(val) => setSelectedYear(val)}
              style={{ width: "100%", textAlign: "center" }}
              allowClear
            >
              {evaluationYears.map((y: any) => (
                <Option key={y.evlYear} value={y.evlYear}>
                  {y.evlYear}년
                </Option>
              ))}
            </Select>
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "11px", marginBottom: 4 }}>피평가자명</div>
            <Input
              placeholder="피평가자명"
              value={evaluateeName}
              onChange={(e) => setEvaluateeName(e.target.value)}
              style={{ width: "100%", textAlign: "center" }}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <div style={{ width: 120 }}>
            <div style={{ fontSize: "11px", marginBottom: 4 }}>직급</div>
            <Select
              placeholder="직급"
              value={position}
              onChange={(val) => setPosition(val ?? "")}
              allowClear
              style={{ width: "100%", textAlign: "center" }}
            >
              <Option value="">전체</Option>
              {(() => {
                const posOrder: { [key: string]: number } = {
                  상무이사: 1,
                  이사: 2,
                  부장: 3,
                  차장: 4,
                  과장: 5,
                  주임: 6,
                };
                return positionOptions
                  .filter(Boolean)
                  .sort((a, b) => (posOrder[a] ?? 99) - (posOrder[b] ?? 99))
                  .map((p) => (
                    <Option key={p} value={p}>
                      {p}
                    </Option>
                  ));
              })()}
            </Select>
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "11px", marginBottom: 4 }}>부서</div>
            <Select
              placeholder="부서"
              value={department}
              onChange={(val) => setDepartment(val ?? "")}
              allowClear
              style={{ width: "100%", textAlign: "center" }}
            >
              <Option value="">전체</Option>
              {departmentOptions
                .sort((a, b) => a.localeCompare(b, "ko"))
                .map((d) => (
                  <Option key={d} value={d}>
                    {d}
                  </Option>
                ))}
            </Select>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
            marginTop: 18,
          }}
        >
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExcelDownload}
            style={
              period === "HR" && evaluationData.length > 0
                ? { backgroundColor: "#1D6F42", color: "white" }
                : {}
            }
            disabled={!(period === "HR" && evaluationData.length > 0)}
          >
            엑셀 다운로드
          </Button>

          <div style={{ display: "flex", gap: 8 }}>
            <Button type="primary" onClick={handleSearch}>
              검색
            </Button>
            <Button onClick={handleReset}>초기화</Button>
          </div>
        </div>
      </Card>

      <Card size="small" style={{ marginBottom: 12 }}>
        <div
          style={{
            borderRadius: "8px",
            fontSize: "11px",
            color: "#666",
          }}
        >
          ※ 업무서약서는 본인 제출 이후 조회 가능
          <br />
          ※ 인사평가는 평가기간 내에만 조회 가능
          <br />
          {/*※ 인사평가는 해당 인원의{" "}
          <span style={{ color: "red" }}>1차/2차 평가자만</span> 조회 가능 */}
          ※ 최종점수는 <span style={{ color: "red" }}>HR 관리자만</span>{" "}
          열람/수정 가능
          {/* <br />※ 최종점수 수정은{" "}
          <span style={{ color: "red" }}>2차 평가 완료 시점부터</span> 가능 */}
          <br />
          {"("} 권한이 없는 경우 {"' - '"}로 표시됨
          {")"}
        </div>
      </Card>

      <div>
        {filteredData.length === 0 ? (
          <Empty description="조회된 데이터가 없습니다." />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="key"
            pagination={{ pageSize: 10 }}
            size="small"
            loading={loading}
            bordered
          />
        )}
      </div>

      {/* 최종점수 수정 모달 */}
      <Modal
        title={
          <div style={{ fontSize: "16px", fontWeight: "bold" }}>
            최종점수 수정
          </div>
        }
        open={isModalOpen}
        onCancel={closeScoreModal}
        width={500}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Button onClick={resetScoreModal}>초기화</Button>
            <div>
              <Button onClick={closeScoreModal} style={{ marginRight: 8 }}>
                취소
              </Button>
              <Button type="primary" onClick={handleSaveScore}>
                저장
              </Button>
            </div>
          </div>
        }
      >
        {editingRecord && (
          <div>
            <div
              style={{
                marginBottom: "16px",
                padding: "12px",
                backgroundColor: "#f5f5f5",
                borderRadius: "6px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: "12px",
                }}
              >
                <div>
                  <div
                    style={{
                      marginBottom: "6px",
                      marginLeft: "4px",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    피평가자
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      textAlign: "center",
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                      padding: "6px 8px",
                    }}
                  >
                    {editingRecord.evaluateeName} ({editingRecord.position})
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      marginBottom: "6px",
                      marginLeft: "4px",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    부서
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      textAlign: "center",
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                      padding: "6px 8px",
                    }}
                  >
                    {editingRecord.department}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      marginBottom: "6px",
                      marginLeft: "4px",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    년도
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      textAlign: "center",
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                      padding: "6px 8px",
                    }}
                  >
                    {editingRecord.evaluationYear}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px", marginBottom: "3px" }}>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    marginBottom: "6px",
                    fontSize: "13px",
                    fontWeight: "bold",
                  }}
                >
                  현재 점수
                </div>
                <Input
                  value={editingRecord.finalScore}
                  disabled
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    textAlign: "center",
                    backgroundColor: "#f0f0f0",
                  }}
                />
              </div>

              <div style={{ flex: 1 }}>
                <div
                  style={{
                    marginBottom: "6px",
                    fontSize: "13px",
                    fontWeight: "bold",
                  }}
                >
                  새로운 점수 <span style={{ color: "red" }}>*</span>
                </div>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={newScore}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "") {
                      setNewScore("");
                      setIsResetMode(false);
                      return;
                    }
                    if (/^\d{0,3}(\.\d{0,2})?$/.test(val)) {
                      setNewScore(val);
                      setIsResetMode(false);
                    }
                  }}
                  onBlur={() => {
                    if (newScore === "" || newScore === ".") {
                      setNewScore("0");
                    }
                  }}
                  placeholder="점수 입력"
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                />
                <div
                  style={{ fontSize: "11px", color: "#999", marginTop: "4px" }}
                >
                  (0~100 범위, 소수점 2자리 입력가능)
                </div>
              </div>
            </div>

            <div style={{ marginBottom: "24px" }}>
              <div
                style={{
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: "bold",
                }}
              >
                수정 사유 <span style={{ color: "red" }}>*</span>
              </div>
              <Input.TextArea
                key={
                  editingRecord.empNo + editingRecord.evaluationYear + resetKey
                }
                defaultValue={scoreReason}
                onChange={(e) => {
                  scoreReasonRef.current = e.target.value;
                  setScoreReasonCount(e.target.value.length);
                  if (e.target.value.trim()) setIsResetMode(false);
                }}
                placeholder="점수 수정 사유를 입력하세요"
                rows={4}
                maxLength={500}
                showCount={{ formatter: () => `${scoreReasonCount} / 500` }}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MobileEvl0103m;
